import React, { useMemo, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  ArrowForwardIos as ArrowForwardIosIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Theme,
  Typography,
} from "@mui/material";
import { isSameMonth, isSameWeek } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { AppointmentStatusKind, UpdateAppointmentInput } from "@/gql/graphql";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import useGlobalPictureInPicture from "@/hooks/usePictureInPicture";
import AppointmentConfirmationModal from "@/pages/Appointments/components/AppointmentConfirmationModal";
import AppointmentPanel from "@/pages/Appointments/components/AppointmentPanel";
import CancelAppointmentModal from "@/pages/Appointments/components/CancelAppointmentModal";
import EditAppointment from "@/pages/Appointments/components/EditAppointment";
import { ConfirmationDetails } from "@/pages/Appointments/types";
import { isAppointmentFinished } from "@/pages/Appointments/utils/isAppointmentFinished";
import { colors } from "@/theme/overrides/colors";
import { format, formatISODate, parseDateTime } from "@/utils/datetime";
import pendingInvitesIcon from "@assets/icons/add-1.png";
import priestIcon from "@assets/icons/priest-1.png";

interface Props {
  loading?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 18,
    height: "100%",
    backgroundColor: colors.white,
    padding: 14,
  },
  myClientCountContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingLeft: 8,
    marginTop: 12,
  },
  myClientCount: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  clientCountText: {
    fontSize: theme.typography.pxToRem(14),
    backgroundColor: colors.primary,
    paddingRight: 3,
    paddingLeft: 3,
    borderRadius: theme.typography.pxToRem(4.8),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  myClientContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    marginBottom: 8,
    padding: 8,
    border: "2px solid #EDF0F5",
    height: theme.typography.pxToRem(144),
    justifyContent: "center",
  },
  myClientInfoContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 14,
  },
  notificationIcon: {
    height: 24,
    width: 24,
    alignSelf: "center",
    marginRight: 32,
  },
  notificationInfoContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  notificationContainer: {
    display: "flex",
    justifyContent: "space-between",
    border: "2px solid #EDF0F5",
    backgroundColor: colors.white,
    marginTop: 14,
    marginBottom: 8,
    paddingLeft: 6,
    paddingRight: 12,
    height: theme.typography.pxToRem(80),
  },
  appointmentPanels: {
    display: "flex",
    flexDirection: "column",
    marginTop: 16,
    gap: 16,
  },
  appointmentsLink: {
    fontSize: 16,
    fontWeight: 700,
    textDecoration: "none",
    color: colors.primary,
  },
}));

const HorizontalDivider = withStyles(Divider, {
  root: {
    height: 3,
    color: "#EDF0F5",
  },
});

const StyledArrowIcon = withStyles(ArrowForwardIosIcon, {
  root: {
    alignSelf: "center",
    marginLeft: 18,
    height: 12,
    width: 12,
    color: colors.primary,
  },
});

const StyledCircularProgress = withStyles(CircularProgress, {
  root: {
    height: ".5rem",
    width: ".5rem",
  },
});

export const clientMetricsFragment = graphql(`
  fragment ClientMetrics on Query {
    clients {
      nodes {
        id
        accountId
        invitedAt
        createdAt
      }
    }
    appointments(status: UPCOMING, date: $currentDate) {
      date
      appointments {
        ...AppointmentPanel
      }
    }
  }
`);

const updateAppointmentMutation = graphql(`
  mutation UpdateAppointment2($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      appointment {
        id
        status
        startTime
        startDate
        endTime
        client {
          id
          profile {
            id
            fullName
          }
        }
      }
    }
  }
`);

const ClientMetrics: React.FC<Props> = ({ loading = false }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const pip = useGlobalPictureInPicture();
  const [appointmentId, setAppointmentId] = useState<string>("");
  const [editVisibility, setEditVisibility] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAppointment] = useMutation(updateAppointmentMutation);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationDetails, setConfirmationDetails] =
    useState<ConfirmationDetails>();
  const [updateAppointment] = useMutation(updateAppointmentMutation);

  const snackbar = useGlobalSnackbar();

  const [data] = useFragment(clientMetricsFragment, "ROOT_QUERY", {
    variables: { currentDate: formatISODate(new Date()) },
  });

  const allClientsCount = useMemo(
    () => data?.clients.nodes.length,
    [data?.clients]
  );
  const clientsThisMonthCount = useMemo(
    () =>
      data?.clients.nodes.filter((client) =>
        isSameMonth(parseDateTime(client.createdAt), new Date())
      ).length,
    [data?.clients]
  );
  const clientsThisWeekCount = useMemo(
    () =>
      data?.clients.nodes.filter((client) =>
        isSameWeek(parseDateTime(client.createdAt), new Date())
      ).length,
    [data?.clients]
  );
  const pendingInvitesCount = useMemo(
    () =>
      data?.clients.nodes.filter(
        (client) => client.invitedAt && !client.accountId
      ).length,
    [data?.clients]
  );

  async function handleEditAppointment(input: UpdateAppointmentInput) {
    try {
      await updateAppointment({
        variables: { input: input },
      });
      setEditVisibility(false);
      snackbar.open("Successfully saved appointment details.");
    } catch (error) {
      snackbar.open(`There was an error in updating your appointment ${error}`);
    }
  }

  async function handleCancelAppointment() {
    const input: UpdateAppointmentInput = {
      id: appointmentId,
      patch: {
        status: AppointmentStatusKind.Cancelled,
      },
    };
    try {
      const result = await cancelAppointment({
        variables: { input },
      });

      setShowCancelModal(false);
      setShowConfirmation(true);
      setConfirmationDetails({
        startDate: format(
          {
            date:
              result.data?.updateAppointment?.appointment?.startDate ??
              new Date(),
          },
          "dd MMMM yyyy"
        ),
        startTime: format(
          {
            timeofday:
              result.data?.updateAppointment?.appointment?.startTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        endTime: format(
          {
            timeofday:
              result.data?.updateAppointment?.appointment?.endTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        fullName:
          result.data?.updateAppointment?.appointment?.client.profile?.fullName,

        status: result.data?.updateAppointment?.appointment?.status,
      });
    } catch (error) {
      snackbar.open(
        `There was an error in cancelling your appointment ${error}`
      );
    }
  }
  function handleMenuClick(type: string, id: string) {
    setAppointmentId(id);
    switch (type) {
      case "edit": {
        setEditVisibility(true);
        break;
      }
      case "cancel": {
        setShowCancelModal(true);
      }
    }
  }

  if (loading && data == null) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <StyledCircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.myClientContainer}>
        <Box className={classes.myClientInfoContainer}>
          <Box className={classes.notificationInfoContainer}>
            <img className={classes.notificationIcon} src={priestIcon} />
            <Typography variant="h6" style={{ fontSize: "1rem" }}>
              My Clients
            </Typography>
          </Box>

          <Box className={classes.notificationInfoContainer}>
            <Typography variant="h6" color="textPrimary">
              {allClientsCount}
            </Typography>
          </Box>
        </Box>
        <HorizontalDivider />

        <Box className={classes.myClientCountContainer}>
          <Box className={classes.myClientCount}>
            <Typography
              variant="body1"
              style={{ fontSize: ".9rem", marginRight: 4 }}
            >
              This week:
            </Typography>
            <Typography variant="body1" className={classes.clientCountText}>
              {clientsThisWeekCount}
            </Typography>
          </Box>

          <Box className={classes.myClientCount}>
            <Typography
              variant="body1"
              style={{ fontSize: ".9rem", marginRight: 4 }}
            >
              This month:
            </Typography>
            <Typography variant="body1" className={classes.clientCountText}>
              {clientsThisMonthCount}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        className={classes.notificationContainer}
        onClick={() => navigate("/clients/invites")}
      >
        <Box className={classes.notificationInfoContainer}>
          <img className={classes.notificationIcon} src={pendingInvitesIcon} />
          <Typography
            variant="h6"
            color="textPrimary"
            style={{ fontSize: "1rem" }}
          >
            Pending invites
          </Typography>
        </Box>
        <Box className={classes.notificationInfoContainer}>
          <Typography variant="h6" color="textPrimary">
            {pendingInvitesCount}
          </Typography>
          <StyledArrowIcon />
        </Box>
      </Box>
      {Features.appointmentsEnabled && (
        <Box className={classes.appointmentPanels}>
          {data?.appointments[0]?.appointments?.map((appointment) => {
            if (
              appointment.status === "cancelled" ||
              isAppointmentFinished(appointment)
            ) {
              return null;
            }

            return (
              <AppointmentPanel
                id={appointment.id}
                onEdit={() => {
                  setAppointmentId(appointment.id);
                  setEditVisibility(true);
                }}
                key={appointment.id}
                onClickMenu={handleMenuClick}
                onJoin={() =>
                  pip.open(appointment.meetingDetails?.roomUrl ?? "")
                }
              />
            );
          })}
          <Box display="flex" alignItems="center">
            <Link className={classes.appointmentsLink} to="/appointments">
              See All Appointments
            </Link>
            <ChevronRightIcon htmlColor={colors.primary} />
          </Box>
        </Box>
      )}
      <EditAppointment
        id={appointmentId}
        open={editVisibility}
        onClose={() => setEditVisibility(false)}
        handleEdit={handleEditAppointment}
        onCancel={() => setShowCancelModal(true)}
      />
      <CancelAppointmentModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        handleCancel={handleCancelAppointment}
      />
      <AppointmentConfirmationModal
        startDate={confirmationDetails?.startDate}
        startTime={confirmationDetails?.startTime}
        endTime={confirmationDetails?.endTime}
        fullName={confirmationDetails?.fullName}
        open={showConfirmation}
        status={confirmationDetails?.status}
        onClose={() => {
          setShowConfirmation(false);
        }}
      />
    </Box>
  );
};

export default ClientMetrics;
